<template>
  <EditView :title="$t('pages.smartpricing.edit.title')" @mounted-handler="onMountedHandler">
    <template #navigationButtons>
      <NavigationLinkButton
        :to="{ name: 'smartpricingRatePlanMapping' }"
        :icon="Icon.NETWORKING"
        :title="$t('pages.smartpricing.ratePlanMapping.title')"
        :subtitle="$t('pages.smartpricing.ratePlanMapping.subTitle')"
      />
      <NavigationLinkButton
        :to="{ name: 'smartpricingDerivationRuleList' }"
        :icon="Icon.CHECKLIST"
        :title="$t('pages.smartpricing.smartpricingDerivationRule.list.title')"
        :subtitle="$t('pages.smartpricing.smartpricingDerivationRule.list.subTitle')"
      />
      <ApiRequestLogLinkButton
        v-if="component && (userType === UserType.ADMIN || userType === UserType.SUPER_ADMIN) && typeof component.id === 'number'"
        :client-app-id="component.id"
      />
    </template>
    <FieldGroup v-if="settings">
      <FormElement :title="$t('message.active')" instance-path="/state">
        <StateActiveInactiveRadioButton v-model="settings.state" />
      </FormElement>
      <FormElement :title="$t('smartpricing.accomodationId')" instance-path="/accomodationId">
        <BaseInput v-model="settings.accomodationId" />
      </FormElement>
    </FieldGroup>

    <FieldGroup v-if="settings && canBeginIntegration">
      <BaseStepper
        v-model="settings.integrationState"
        :steps="steps"
        :is-linear="userType !== UserType.SUPER_ADMIN"
        :is-vertical="true"
      >
        <template #panel-new="{ activate }">
          <div class="flex flex-col gap-4">
            <template v-if="!areRatePlansMapped">
              <div>
                {{ $t("smartpricing.integrationSteps.selectRatePlans.instruction") }}
              </div>
              <div class="flex justify-center">
                <RouterLink :to="{ name: 'smartpricingRatePlanMapping' }">
                  <BaseButton :is-highlighted="true">{{ $t("pages.smartpricing.ratePlanMapping.subTitle") }} </BaseButton>
                </RouterLink>
              </div>
            </template>
            <template v-else>
              <div>
                {{ $t("smartpricing.integrationSteps.selectRatePlans.completed") }}
              </div>
              <div class="flex justify-center gap-4">
                <BaseButton
                  :is-highlighted="true"
                  @click="setIntegrationState({ state: SmartpricingIntegrationState.RATE_PLANS_SELECTED, activate })"
                  >{{ $t("common.continue") }} →</BaseButton
                >
              </div>
            </template>
          </div>
        </template>
        <template #panel-rate_plans_selected="{ activate }">
          <div class="flex flex-col gap-4">
            <div>{{ $t("smartpricing.integrationSteps.sendRoomsAndRates.instruction") }}</div>
            <div class="flex items-center justify-center gap-4">
              <BaseButton
                :is-highlighted="true"
                @click="setIntegrationState({ state: SmartpricingIntegrationState.NEW, activate })"
              >
                ← {{ $t("common.back") }}
              </BaseButton>
              <BaseButton @click="setIntegrationState({ state: SmartpricingIntegrationState.ROOMS_AND_RATES_SENT, activate })">
                {{ $t("smartpricing.integrationSteps.sendRoomsAndRates.submit") }} →
              </BaseButton>
            </div>
          </div>
        </template>

        <template #panel-rooms_and_rates_sent="{ activate }">
          <div class="flex flex-col gap-4">
            <div>
              {{ $t("smartpricing.integrationSteps.confirmRoomsAndRates.instruction") }}<br />
              <strong>{{ $t("smartpricing.integrationSteps.confirmRoomsAndRates.warning") }}</strong>
            </div>
            <div>
              <BaseCheckbox v-model="areRoomsAndRatesConfirmed">
                {{ $t("smartpricing.integrationSteps.confirmRoomsAndRates.checkbox") }}
              </BaseCheckbox>
            </div>
            <div class="flex items-center justify-center gap-4">
              <BaseButton
                :is-highlighted="true"
                @click="setIntegrationState({ state: SmartpricingIntegrationState.RATE_PLANS_SELECTED, activate })"
              >
                ← {{ $t("common.back") }}
              </BaseButton>
              <BaseButton
                :disabled="!areRoomsAndRatesConfirmed"
                @click="setIntegrationState({ state: SmartpricingIntegrationState.ROOMS_AND_RATES_CONFIRMED, activate })"
              >
                {{ $t("common.continue") }} →
              </BaseButton>
            </div>
          </div>
        </template>

        <template #panel-rooms_and_rates_confirmed="{ activate }">
          <div class="flex flex-col gap-4">
            <div>{{ $t("smartpricing.integrationSteps.sendBulkReservations.instruction") }}</div>
            <div class="flex items-center justify-center gap-4">
              <BaseButton @click="setIntegrationState({ state: SmartpricingIntegrationState.BULK_RESERVATIONS_SENT, activate })">
                {{ $t("smartpricing.integrationSteps.sendBulkReservations.submit") }} →
              </BaseButton>
            </div>
          </div>
        </template>

        <template #panel-bulk_reservations_sent="{ activate }">
          <div class="flex flex-col gap-4">
            <div>
              {{ $t("smartpricing.integrationSteps.confirmBulkReservations.instruction") }}<br />
              <strong>{{ $t("smartpricing.integrationSteps.confirmBulkReservations.warning") }}</strong>
            </div>
            <div>
              <BaseCheckbox v-model="areBulkReservationsConfirmed">
                {{ $t("smartpricing.integrationSteps.confirmBulkReservations.checkbox") }}
              </BaseCheckbox>
            </div>
            <div class="flex items-center justify-center gap-4">
              <BaseButton
                :is-highlighted="true"
                @click="setIntegrationState({ state: SmartpricingIntegrationState.ROOMS_AND_RATES_CONFIRMED, activate })"
              >
                ← {{ $t("common.back") }}
              </BaseButton>
              <BaseButton
                :disabled="!areBulkReservationsConfirmed"
                @click="setIntegrationState({ state: SmartpricingIntegrationState.BULK_RESERVATIONS_CONFIRMED, activate })"
              >
                {{ $t("common.continue") }} →
              </BaseButton>
            </div>
          </div>
        </template>
        <template #panel-bulk_reservations_confirmed="{ activate }">
          <div class="flex flex-col gap-4">
            <div>{{ $t("smartpricing.integrationSteps.fetchPrices.instruction") }}</div>
            <div class="flex items-center justify-center gap-4">
              <BaseButton @click="setIntegrationState({ state: SmartpricingIntegrationState.COMPLETED, activate })">
                {{ $t("smartpricing.integrationSteps.fetchPrices.submit") }} →
              </BaseButton>
            </div>
          </div>
        </template>
        <template #panel-completed>
          <div>{{ $t("smartpricing.integrationSteps.completed.message") }}</div>
        </template>
      </BaseStepper>
    </FieldGroup>
    <div v-else>
      <div>{{ $t("smartpricing.integrationSteps.saveAccomodationId") }}</div>
    </div>
    <SaveCloseComponent @save="saveSettings" @save-close="saveSettings(true)" />
  </EditView>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, defineAsyncComponent, Ref, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import ApiRequestLogLinkButton from "../../apiRequestLog/components/ApiRequestLogLinkButton.vue";
import { Icon } from "../../assets/icons/Icon";
import { ComponentDto } from "../../component/dto.component";
import { ComponentType } from "../../component/enum.componentType";
import { useComponent } from "../../component/service.component";
import BaseButton from "../../components/BaseButton.vue";
import BaseCheckbox from "../../components/BaseCheckbox.vue";
import BaseInput from "../../components/BaseInput.vue";
import BaseStepper from "../../components/BaseStepper.vue";
import EditView from "../../components/EditView.vue";
import FieldGroup from "../../components/FieldGroup.vue";
import FormElement from "../../components/FormElement.vue";
import NavigationLinkButton from "../../components/NavigationLinkButton.vue";
import StateActiveInactiveRadioButton from "../../components/StateActiveInactiveRadioButton.vue";
import { useUser } from "../../composition-functions/user";
import { UserType } from "../../composition-functions/user/UserType";
import { initAjvErrors } from "../../errors/useErrors";
import { getGlobalTranslation } from "../../i18n";
import { useMandant } from "../../mandant/service.mandant";
import { assertNever } from "../../util/assertNever";
import { isNullOrWhitespace } from "../../util/isNullOrWhitespace";
import { setToastMessage, setToastMessageLoading } from "../../util/SweetalertHelper";
import { SmartpricingSettingsDto } from "../dto.smartpricingSettings";
import { SmartpricingIntegrationState } from "../enum.smartpricingIntegrationState";
import { getRatePlanMappings } from "../service.ratePlanMapping";
import { setupIntegration } from "../service.smartpricing";
import { getSmartpricingSettings, setSmartpricingSettings } from "../service.smartpricingSettings";

const SaveCloseComponent = defineAsyncComponent(() => import("../../components/SaveCloseComponent.vue"));

defineEmits<{
  saveRecord: [value: { shouldReturnToParent: boolean }];
}>();

const settings: Ref<null | SmartpricingSettingsDto> = ref(null);

const { t } = useI18n();
const router = useRouter();
const { provideAjvErrors } = initAjvErrors();
const mandantStore = useMandant();
const { currentMandant } = storeToRefs(mandantStore);
const { getComponent } = useComponent();
const { userType } = useUser();

const component = ref<null | ComponentDto>(null);
const savedAccomodationId = ref<null | string>(null);
const areRatePlansMapped = ref(false);
const areRoomsAndRatesConfirmed = ref(false);
const areBulkReservationsConfirmed = ref(false);
const areRoomsAndRatesSent = ref(false);
const areBulkReservationsSent = ref(false);
const arePricesFetched = ref(false);
const isAccomodationIdChanged = computed(() => savedAccomodationId.value !== settings.value?.accomodationId);
const canBeginIntegration = computed(() => !isNullOrWhitespace(settings.value?.accomodationId) && !isAccomodationIdChanged.value);

const mandantId = currentMandant.value!.id;

const steps = Object.values(SmartpricingIntegrationState).map((state, _index) => ({
  value: state,
  label: t(`smartpricing.integrationStateOptions.${state}`),
}));

type SetIntegrationStateParams = {
  state: SmartpricingIntegrationState;
  activate: (value: number | string) => void;
};
async function setIntegrationState({ state: desiredIntegrationState, activate }: SetIntegrationStateParams) {
  switch (desiredIntegrationState) {
    case SmartpricingIntegrationState.NEW:
      await setupIntegration({ activate, mandantId, step: 1, desiredIntegrationState });
      break;
    case SmartpricingIntegrationState.RATE_PLANS_SELECTED:
      await setupIntegration({ activate, mandantId, step: 2, desiredIntegrationState });
      break;
    case SmartpricingIntegrationState.ROOMS_AND_RATES_SENT:
      await setupIntegration({ activate, mandantId, step: 3, desiredIntegrationState });
      break;
    case SmartpricingIntegrationState.ROOMS_AND_RATES_CONFIRMED:
      await setupIntegration({ activate, mandantId, step: 4, desiredIntegrationState });
      break;
    case SmartpricingIntegrationState.BULK_RESERVATIONS_SENT:
      await setupIntegration({ activate, mandantId, step: 5, desiredIntegrationState });
      break;
    case SmartpricingIntegrationState.BULK_RESERVATIONS_CONFIRMED:
      await setupIntegration({ activate, mandantId, step: 6, desiredIntegrationState });
      break;
    case SmartpricingIntegrationState.COMPLETED:
      await setupIntegration({ activate, mandantId, step: 7, desiredIntegrationState });
      break;
    default:
      assertNever(desiredIntegrationState);
  }
}

async function saveSettings(shouldReturnToParent: boolean = false) {
  if (!settings.value) {
    return;
  }
  const mandantStore = useMandant();
  const { currentMandant } = storeToRefs(mandantStore);
  setToastMessageLoading();
  settings.value = await provideAjvErrors(setSmartpricingSettings)({
    mandantId: currentMandant.value!.id,
    smartpricingSettingsDto: settings.value,
  });
  savedAccomodationId.value = settings.value?.accomodationId;
  setToastMessage({ icon: "success", title: getGlobalTranslation({ message: "message.recordSaved" }) });

  if (shouldReturnToParent) {
    router.push({ name: "mandantDashboard" });
  }
}

function initializeIntegrationFlags(integrationState: SmartpricingIntegrationState) {
  const states = Object.values(SmartpricingIntegrationState);
  const currentStateIndex = states.indexOf(integrationState);
  const isAtLeast = (state: SmartpricingIntegrationState) => currentStateIndex >= states.indexOf(state);

  areRatePlansMapped.value = isAtLeast(SmartpricingIntegrationState.RATE_PLANS_SELECTED);
  areRoomsAndRatesSent.value = isAtLeast(SmartpricingIntegrationState.ROOMS_AND_RATES_SENT);
  areRoomsAndRatesConfirmed.value = isAtLeast(SmartpricingIntegrationState.ROOMS_AND_RATES_CONFIRMED);
  areBulkReservationsSent.value = isAtLeast(SmartpricingIntegrationState.BULK_RESERVATIONS_SENT);
  areBulkReservationsConfirmed.value = isAtLeast(SmartpricingIntegrationState.BULK_RESERVATIONS_CONFIRMED);
  arePricesFetched.value = isAtLeast(SmartpricingIntegrationState.COMPLETED);
}

async function onMountedHandler() {
  const mandantStore = useMandant();
  const { currentMandant } = storeToRefs(mandantStore);
  settings.value = await getSmartpricingSettings(currentMandant.value!.id);
  savedAccomodationId.value = settings.value?.accomodationId;

  initializeIntegrationFlags(settings.value.integrationState);

  if (settings.value.integrationState === SmartpricingIntegrationState.NEW) {
    const ratePlanMappingDtos = await getRatePlanMappings(currentMandant.value!.id);
    if (ratePlanMappingDtos.some((ratePlanMappingDto) => ratePlanMappingDto.smartpricingDerivationRule !== null)) {
      areRatePlansMapped.value = true;
    }
  }
  component.value = await getComponent({
    mandantId: currentMandant.value!.id,
    componentIdOrComponentType: ComponentType.SMARTPRICING,
  });
}
</script>
